import { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const MINUTE_STEPS = [0, 15, 30, 45];

function parse24h(value: string): { hour: number; minuteIdx: number; ampm: 'AM' | 'PM' } {
  const [h, m] = value.split(':').map(Number);
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const minuteIdx = Math.max(0, MINUTE_STEPS.indexOf(m));
  return { hour, minuteIdx, ampm };
}

function to24h(hour: number, minuteIdx: number, ampm: 'AM' | 'PM'): string {
  let h = hour;
  if (ampm === 'AM' && hour === 12) h = 0;
  if (ampm === 'PM' && hour !== 12) h = hour + 12;
  return `${String(h).padStart(2, '0')}:${String(MINUTE_STEPS[minuteIdx]).padStart(2, '0')}`;
}

// Round a "HH:MM" string up to the next 15-minute slot
function roundUpToSlot(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const slotIdx = MINUTE_STEPS.findIndex((s) => s >= m);
  if (slotIdx === -1) {
    const nextH = (h + 1) % 24;
    return `${String(nextH).padStart(2, '0')}:00`;
  }
  return `${String(h).padStart(2, '0')}:${String(MINUTE_STEPS[slotIdx]).padStart(2, '0')}`;
}

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  label?: string;
  minTime?: string; // "HH:MM" 24h — times before this are skipped
}

export function TimePicker({ value, onChange, label, minTime }: TimePickerProps) {
  const [hour,      setHour]      = useState(8);
  const [minuteIdx, setMinuteIdx] = useState(0);
  const [ampm,      setAmpm]      = useState<'AM' | 'PM'>('AM');

  useEffect(() => {
    if (value) {
      const p = parse24h(value);
      setHour(p.hour);
      setMinuteIdx(p.minuteIdx);
      setAmpm(p.ampm);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When minTime appears or changes, snap to the next valid slot if current display is behind it
  useEffect(() => {
    if (!minTime) return;
    const current = to24h(hour, minuteIdx, ampm);
    if (current < minTime) {
      const snapped = roundUpToSlot(minTime);
      const p = parse24h(snapped);
      setHour(p.hour);
      setMinuteIdx(p.minuteIdx);
      setAmpm(p.ampm);
      onChange(snapped);
    }
  }, [minTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only move the displayed drum (and fire onChange) when the candidate time is still valid —
  // previously the digits updated unconditionally and only the onChange call was blocked, so you
  // could scroll the drum to show a past time that silently wasn't the actual selected value.
  const applyChange = (h: number, mi: number, ap: 'AM' | 'PM') => {
    const t = to24h(h, mi, ap);
    if (minTime && t < minTime) return;
    setHour(h);
    setMinuteIdx(mi);
    setAmpm(ap);
    onChange(t);
  };

  const stepHourUp = () => {
    applyChange(hour === 12 ? 1 : hour + 1, minuteIdx, ampm);
  };
  const stepHourDown = () => {
    applyChange(hour === 1 ? 12 : hour - 1, minuteIdx, ampm);
  };
  const stepMinuteUp = () => {
    applyChange(hour, (minuteIdx + 1) % 4, ampm);
  };
  const stepMinuteDown = () => {
    applyChange(hour, (minuteIdx + 3) % 4, ampm);
  };
  const toggleAmPm = (val: 'AM' | 'PM') => {
    applyChange(hour, minuteIdx, val);
  };

  const btnCls = 'flex items-center justify-center h-8 w-8 rounded-lg text-muted hover:text-text hover:bg-surface-raised transition-colors';
  const drumCls = 'w-14 text-center text-2xl font-black text-text tabular-nums select-none';

  return (
    <div>
      {label && (
        <p className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
          {label}
        </p>
      )}
      <div className="inline-flex items-center gap-4 rounded-xl bg-bg px-5 py-4">

        {/* Hours */}
        <div className="flex flex-col items-center gap-1">
          <button type="button" className={btnCls} onClick={stepHourUp} aria-label="Increase hour">
            <ChevronUp size={16} />
          </button>
          <span className={drumCls} aria-live="polite">{String(hour).padStart(2, '0')}</span>
          <button type="button" className={btnCls} onClick={stepHourDown} aria-label="Decrease hour">
            <ChevronDown size={16} />
          </button>
        </div>

        <span className="text-2xl font-black text-muted pb-0.5">:</span>

        {/* Minutes */}
        <div className="flex flex-col items-center gap-1">
          <button type="button" className={btnCls} onClick={stepMinuteUp} aria-label="Increase minute">
            <ChevronUp size={16} />
          </button>
          <span className={drumCls} aria-live="polite">
            {String(MINUTE_STEPS[minuteIdx]).padStart(2, '0')}
          </span>
          <button type="button" className={btnCls} onClick={stepMinuteDown} aria-label="Decrease minute">
            <ChevronDown size={16} />
          </button>
        </div>

        {/* AM / PM pill */}
        <div className="flex flex-col rounded-lg bg-surface-raised overflow-hidden ml-2">
          {(['AM', 'PM'] as const).map((seg) => (
            <button
              key={seg}
              type="button"
              onClick={() => toggleAmPm(seg)}
              className={`px-3 py-2 text-xs font-bold transition-colors ${
                ampm === seg
                  ? 'bg-accent text-black'
                  : 'text-muted hover:text-text hover:bg-surface-raised'
              }`}
            >
              {seg}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
